import React, { useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
} from 'recharts';
import { buildSparklineData, formatTrendRange } from '../summaryFormatters';
import { formatAxisTick } from '../../../utils/formatInTimezone';

const WIDTH = 88;
const HEIGHT = 28;
const CURRENT_COLOR = '#2563eb';
const PREVIOUS_COLOR = '#94a3b8';
// Подписи над каждой точкой читаются, пока точек немного; дальше — только hover.
const MAX_LABELED_POINTS = 31;

/**
 * Мини-график нарушений по trend_points строки таблицы: сплошная линия — текущий
 * период, пунктир — прошлый. Фиксированный размер без осей, чтобы не зависеть
 * от ResponsiveContainer.
 *
 * ТЗ V5 №4: по клику открывается крупный график тех же точек — с осью времени,
 * подписями значений и границами корзины в подсказке. Данные те же, что у
 * мини-графика: новых запросов нет.
 */
const Sparkline = ({ points, previousPoints, title, timeZone }) => {
  const [open, setOpen] = useState(false);
  const data = useMemo(() => buildSparklineData(points, previousPoints), [points, previousPoints]);
  const hasPrevious = Array.isArray(previousPoints) && previousPoints.length > 1;

  const detailed = useMemo(() => (Array.isArray(points) ? points : []).map((point, index) => ({
    from: point.from,
    to: point.to,
    current: data[index]?.current ?? null,
    previous: data[index]?.previous ?? null,
    previousFrom: previousPoints?.[index]?.from,
    previousTo: previousPoints?.[index]?.to,
  })), [points, previousPoints, data]);

  if (!Array.isArray(points) || points.length < 2) return <span>—</span>;

  const tz = timeZone || 'UTC';
  const spanMs = new Date(points[points.length - 1].to || points[points.length - 1].from) - new Date(points[0].from);
  const withDate = !(spanMs <= 86400000);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <div className="sum-tooltip">
        <div className="sum-tooltip-range">{formatTrendRange(point.from, point.to, tz)}</div>
        <div><b>{point.current ?? '—'}</b></div>
        {hasPrevious && point.previousFrom && (
          <div className="sum-tooltip-previous">
            <div className="sum-tooltip-range">{formatTrendRange(point.previousFrom, point.previousTo, tz)}</div>
            <div>{point.previous ?? '—'}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className="sum-sparkline"
        title="Открыть график"
        aria-label={title ? `Открыть график. ${title}` : 'Открыть график'}
        onClick={() => setOpen(true)}
      >
        <LineChart width={WIDTH} height={HEIGHT} data={data} margin={{ top: 3, right: 2, bottom: 3, left: 2 }}>
          {hasPrevious && (
            <Line
              type="monotone"
              dataKey="previous"
              stroke={PREVIOUS_COLOR}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              isAnimationActive={false}
            />
          )}
          <Line
            type="monotone"
            dataKey="current"
            stroke={CURRENT_COLOR}
            strokeWidth={1.75}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </button>

      <Modal show={open} onHide={() => setOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="sum-spark-modal-title">{title || 'Тренд'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="sum-trend-legend">
            <span className="sum-trend-key">
              <span className="sum-trend-swatch" style={{ background: CURRENT_COLOR }} aria-hidden="true" />
              Выбранный период
            </span>
            {hasPrevious && (
              <span className="sum-trend-key">
                <span className="sum-trend-swatch sum-trend-swatch--dashed" style={{ borderColor: PREVIOUS_COLOR }} aria-hidden="true" />
                Предыдущий период
              </span>
            )}
          </div>
          <div className="sum-spark-modal-chart">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={detailed} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="from"
                  tickFormatter={(iso) => formatAxisTick(iso, tz, withDate)}
                  minTickGap={24}
                  fontSize={12}
                />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip content={renderTooltip} />
                {hasPrevious && (
                  <Line
                    type="linear"
                    dataKey="previous"
                    stroke={PREVIOUS_COLOR}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                )}
                <Line
                  type="linear"
                  dataKey="current"
                  stroke={CURRENT_COLOR}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                  isAnimationActive={false}
                >
                  {detailed.length <= MAX_LABELED_POINTS && (
                    <LabelList dataKey="current" position="top" fontSize={12} />
                  )}
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Sparkline;
